'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const setAnimState = (el: HTMLElement, state: 'in' | 'out') => {
  el.dataset.animState = state;
};

const applyAnimVars = (el: HTMLElement) => {
  const { animDelay, animDuration, animEase } = el.dataset;
  if (animDelay) {
    el.style.setProperty('--anim-delay', `${animDelay}ms`);
  }
  if (animDuration) {
    const parsed = Number(animDuration);
    const adjusted = Number.isFinite(parsed) ? Math.min(parsed + 400, 1280) : animDuration;
    el.style.setProperty('--anim-duration', `${adjusted}ms`);
  }
  if (animEase) {
    el.style.setProperty('--anim-ease', animEase);
  }
};

const getObserverOptions = (el: HTMLElement) => {
  const trigger = el.dataset.animTrigger || 'scroll';
  const animId = el.dataset.animId || '';

  if (animId === 'bop-calculator-section' || animId === 'bop-calculator-head') {
    return {
      threshold: 0.2,
      rootMargin: '0px 0px -25% 0px',
    } as const;
  }

  if (animId === 'bop-workflow-section' || animId === 'bop-workflow-head' || animId === 'bop-workflow-list') {
    return {
      threshold: 0.2,
      rootMargin: '0px 0px -30% 0px',
    } as const;
  }

  if (trigger === 'scroll') {
    return {
      threshold: 0.05,
      rootMargin: '0px 0px -10% 0px',
    } as const;
  }

  return null;
};

export default function AnimationProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = searchParams.toString();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('anim-ready');

    const observerMap = new Map<string, IntersectionObserver>();

    const observed = new WeakSet<HTMLElement>();
    const reduceMotion = prefersReducedMotion();

    const setupElement = (el: HTMLElement) => {
      if (observed.has(el)) return;
      observed.add(el);
      applyAnimVars(el);

      if (reduceMotion) {
        setAnimState(el, 'in');
        return;
      }

      const trigger = el.dataset.animTrigger || 'scroll';
      if (trigger === 'load') {
        requestAnimationFrame(() => {
          setAnimState(el, 'in');
        });
        return;
      }

      const options = getObserverOptions(el);
      if (!options) return;

      const key = `${options.threshold}-${options.rootMargin}`;
      let observer = observerMap.get(key);
      if (!observer) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const observedEl = entry.target as HTMLElement;
              setAnimState(observedEl, 'in');
              observer?.unobserve(observedEl);
            });
          },
          options
        );
        observerMap.set(key, observer);
      }

      if (root.classList.contains('nav-resetting')) {
        window.setTimeout(() => {
          observer?.observe(el);
        }, 180);
        return;
      }

      observer.observe(el);
    };

    const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-anim]'));
    elements.forEach(setupElement);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.hasAttribute('data-anim')) {
            setupElement(node);
          }
          node.querySelectorAll?.('[data-anim]').forEach((child) => setupElement(child as HTMLElement));
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observerMap.forEach((observer) => observer.disconnect());
      mutationObserver.disconnect();
    };
  }, [pathname, searchKey]);

  return null;
}
